/**
 * AggregatedPlanData — mirrors iOS Models/AggregatedPlanData.swift.
 * Parsed from GET /plan/{id} `result` JSON string.
 */

export type PlanTimeSlot =
  | 'breakfast'
  | 'morning'
  | 'lunch'
  | 'afternoon'
  | 'dinner'
  | 'evening'
  | 'night';

export const PLAN_TIME_SLOTS: PlanTimeSlot[] = [
  'breakfast',
  'morning',
  'lunch',
  'afternoon',
  'dinner',
  'evening',
];

export interface PlanGeocode {
  latitude: number;
  longitude: number;
}

export interface AggregatedPlanItem {
  id: string;
  title: string;
  normalizedTitle: string;
  description?: string | null;
  matchScore: number;
  reasoning?: string | null;
  itemType: string;
  timeSlot: string;
  startTime?: string | null;
  endTime?: string | null;
  suggestedDurationMinutes?: number | null;
  location?: string | null;
  area?: string | null;
  url?: string | null;
  tags: string[];
  priceHint?: string | null;
  priceRange?: string | null;
  cuisine?: string | null;
  personaCount: number;
  allTags: string[];
  allReasonings: string[];
  operatingHours?: string | null;
  recommendedTimes?: string[] | null;
  bestTimeReason?: string | null;
  slotPriority?: number | null;
  geocode?: PlanGeocode | null;
  isOption: boolean;
  refId?: string | null;
  reservationRequirement?: string | null;
}

export interface AggregatedMustSeeItem {
  id: string;
  title: string;
  normalizedTitle: string;
  whyImportant?: string | null;
  area?: string | null;
  url?: string | null;
  tags: string[];
  matchScore: number;
  personaCount: number;
  recommendedTimes?: string[] | null;
  originalId?: string | null;
}

export interface AggregatedHotelItem {
  id: string;
  name: string;
  normalizedName: string;
  description?: string | null;
  area?: string | null;
  url?: string | null;
  pricePerNight?: number | null;
  currency?: string | null;
  amenities: string[];
  rating?: number | null;
  matchScore: number;
  personaCount: number;
}

export interface AggregatedCatalogActivity {
  id: string;
  title: string;
  normalizedTitle: string;
  description?: string | null;
  area?: string | null;
  url?: string | null;
  tags: string[];
  matchScore: number;
}

export interface AggregatedCatalogMeal {
  id: string;
  title: string;
  normalizedTitle: string;
  description?: string | null;
  cuisine?: string | null;
  area?: string | null;
  url?: string | null;
  tags: string[];
  matchScore: number;
}

export interface AlternateStayArea {
  name: string;
  latitude: number;
  longitude: number;
  radiusKm?: number | null;
}

export interface RecommendedStayArea {
  name: string;
  latitude: number;
  longitude: number;
  radiusKm?: number | null;
  rationale?: string | null;
  alternateAreas?: AlternateStayArea[] | null;
}

/** Day block from primaryItineraryDays — loosely typed for Save Day. */
export interface PlanItineraryDayBlock {
  date?: string | null;
  title?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  itemType?: string | null;
  location?: string | null;
  description?: string | null;
  url?: string | null;
  timeSlot?: string | null;
  normalizedTitle?: string | null;
  reservationRequirement?: string | null;
  [key: string]: unknown;
}

export interface PlanItineraryDay {
  date: string;
  blocks: PlanItineraryDayBlock[];
}

export interface AggregatedPlanData {
  version: number;
  cityName: string;
  dateRange: string;
  startDate: string;
  endDate: string;
  slotOptions: Record<string, AggregatedPlanItem[]>;
  mustSee: AggregatedMustSeeItem[];
  hotels: AggregatedHotelItem[];
  recommendedStayArea?: RecommendedStayArea | null;
  activityCatalog?: AggregatedCatalogActivity[] | null;
  mealCatalog?: AggregatedCatalogMeal[] | null;
  primaryItineraryDays?: PlanItineraryDay[] | null;
  summary?: string | null;
  generatedDate?: string | null;
}

function asRecord(raw: unknown): Record<string, unknown> {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }
  return {};
}

function str(raw: unknown, fallback = ''): string {
  if (raw == null) return fallback;
  return String(raw);
}

function strOrNull(raw: unknown): string | null {
  if (raw == null) return null;
  const s = String(raw).trim();
  return s ? s : null;
}

function num(raw: unknown, fallback = 0): number {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  const n = Number(raw);
  return Number.isFinite(n) ? n : fallback;
}

function numOrNull(raw: unknown): number | null {
  if (raw == null) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function strArray(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((x) => String(x)).filter(Boolean);
}

function newId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `plan-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * iOS ExtendedDurationPlanningService cleans escaped quotes/newlines before decode.
 */
export function cleanPlanResultString(raw: string): string {
  return raw.replace(/\\"/g, '"').replace(/\\n/g, ' ');
}

function parseGeocode(raw: unknown): PlanGeocode | null {
  const o = asRecord(raw);
  const latitude = numOrNull(o.latitude ?? o.lat);
  const longitude = numOrNull(o.longitude ?? o.lng ?? o.lon);
  if (latitude == null || longitude == null) return null;
  return { latitude, longitude };
}

function parsePlanItem(raw: unknown): AggregatedPlanItem | null {
  const o = asRecord(raw);
  const title = str(o.title).trim();
  if (!title) return null;
  const normalizedTitle = str(o.normalizedTitle, title).trim() || title;
  return {
    id: str(o.id) || newId(),
    title,
    normalizedTitle,
    description: strOrNull(o.description),
    matchScore: num(o.matchScore, 0),
    reasoning: strOrNull(o.reasoning),
    itemType: str(o.itemType, 'activity'),
    timeSlot: str(o.timeSlot, 'morning'),
    startTime: strOrNull(o.startTime),
    endTime: strOrNull(o.endTime),
    suggestedDurationMinutes: numOrNull(o.suggestedDurationMinutes),
    location: strOrNull(o.location),
    area: strOrNull(o.area),
    url: strOrNull(o.url),
    tags: strArray(o.tags),
    priceHint: strOrNull(o.priceHint),
    priceRange: strOrNull(o.priceRange),
    cuisine: strOrNull(o.cuisine),
    personaCount: num(o.personaCount, 1),
    allTags: strArray(o.allTags),
    allReasonings: strArray(o.allReasonings),
    operatingHours: strOrNull(o.operatingHours),
    recommendedTimes: Array.isArray(o.recommendedTimes)
      ? strArray(o.recommendedTimes)
      : null,
    bestTimeReason: strOrNull(o.bestTimeReason),
    slotPriority: numOrNull(o.slotPriority),
    geocode: parseGeocode(o.geocode),
    isOption: Boolean(o.isOption),
    refId: strOrNull(o.refId),
    reservationRequirement: strOrNull(o.reservationRequirement),
  };
}

function parseMustSee(raw: unknown): AggregatedMustSeeItem | null {
  const o = asRecord(raw);
  const title = str(o.title).trim();
  if (!title) return null;
  return {
    id: str(o.id) || newId(),
    title,
    normalizedTitle: str(o.normalizedTitle, title).trim() || title,
    whyImportant: strOrNull(o.whyImportant),
    area: strOrNull(o.area),
    url: strOrNull(o.url),
    tags: strArray(o.tags),
    matchScore: num(o.matchScore, 0),
    personaCount: num(o.personaCount, 1),
    recommendedTimes: Array.isArray(o.recommendedTimes)
      ? strArray(o.recommendedTimes)
      : null,
    originalId: strOrNull(o.originalId),
  };
}

function parseHotel(raw: unknown): AggregatedHotelItem | null {
  const o = asRecord(raw);
  const name = str(o.name ?? o.title).trim();
  if (!name) return null;
  return {
    id: str(o.id) || newId(),
    name,
    normalizedName: str(o.normalizedName ?? o.normalizedTitle, name).trim() || name,
    description: strOrNull(o.description),
    area: strOrNull(o.area),
    url: strOrNull(o.url),
    pricePerNight: numOrNull(o.pricePerNight),
    currency: strOrNull(o.currency),
    amenities: strArray(o.amenities),
    rating: numOrNull(o.rating),
    matchScore: num(o.matchScore, 0),
    personaCount: num(o.personaCount, 1),
  };
}

function parseStayArea(raw: unknown): RecommendedStayArea | null {
  if (raw == null) return null;
  const o = asRecord(raw);
  const name = str(o.name).trim();
  const latitude = numOrNull(o.latitude);
  const longitude = numOrNull(o.longitude);
  if (!name || latitude == null || longitude == null) return null;
  const alternates = Array.isArray(o.alternateAreas)
    ? o.alternateAreas
        .map((a): AlternateStayArea | null => {
          const r = asRecord(a);
          const n = str(r.name).trim();
          const lat = numOrNull(r.latitude);
          const lon = numOrNull(r.longitude);
          if (!n || lat == null || lon == null) return null;
          return {
            name: n,
            latitude: lat,
            longitude: lon,
            radiusKm: numOrNull(r.radiusKm),
          };
        })
        .filter((x): x is AlternateStayArea => x != null)
    : null;
  return {
    name,
    latitude,
    longitude,
    radiusKm: numOrNull(o.radiusKm),
    rationale: strOrNull(o.rationale),
    alternateAreas: alternates,
  };
}

function parseItineraryDays(raw: unknown): PlanItineraryDay[] | null {
  if (!Array.isArray(raw)) return null;
  const days: PlanItineraryDay[] = [];
  for (const entry of raw) {
    const o = asRecord(entry);
    const date = str(o.date ?? o.dayDate).slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) continue;
    const blocksRaw = Array.isArray(o.blocks)
      ? o.blocks
      : Array.isArray(o.items)
        ? o.items
        : [];
    const blocks: PlanItineraryDayBlock[] = blocksRaw.map((b) => {
      const r = asRecord(b);
      return {
        ...r,
        date: strOrNull(r.date) ?? date,
        title: strOrNull(r.title),
        startTime: strOrNull(r.startTime),
        endTime: strOrNull(r.endTime),
        itemType: strOrNull(r.itemType),
        location: strOrNull(r.location),
        description: strOrNull(r.description),
        url: strOrNull(r.url),
        timeSlot: strOrNull(r.timeSlot),
        normalizedTitle: strOrNull(r.normalizedTitle),
        reservationRequirement: strOrNull(r.reservationRequirement),
      };
    });
    days.push({ date, blocks });
  }
  return days.length > 0 ? days : null;
}

function parseSlotOptions(raw: unknown): Record<string, AggregatedPlanItem[]> {
  const o = asRecord(raw);
  const out: Record<string, AggregatedPlanItem[]> = {};
  for (const [slot, items] of Object.entries(o)) {
    if (!Array.isArray(items)) continue;
    out[slot] = items
      .map((item) => parsePlanItem(item))
      .filter((item): item is AggregatedPlanItem => item != null);
  }
  return out;
}

/**
 * Parse AggregatedPlanData from an API result string or already-parsed object.
 * Returns null on garbage input (never throws).
 */
export function parseAggregatedPlanData(raw: unknown): AggregatedPlanData | null {
  let value: unknown = raw;
  if (typeof raw === 'string') {
    const cleaned = cleanPlanResultString(raw.trim());
    if (!cleaned) return null;
    try {
      value = JSON.parse(cleaned);
    } catch {
      try {
        value = JSON.parse(raw.trim());
      } catch {
        return null;
      }
    }
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const o = value as Record<string, unknown>;

  const cityName = str(o.cityName).trim();
  const startDate = str(o.startDate).slice(0, 10);
  const endDate = str(o.endDate || o.startDate).slice(0, 10);
  if (!cityName && !startDate && Object.keys(parseSlotOptions(o.slotOptions)).length === 0) {
    // Allow minimal plans that only have slots; reject empty objects.
    if (!o.slotOptions && !o.mustSee && !o.primaryItineraryDays) return null;
  }

  return {
    version: num(o.version, 1),
    cityName: cityName || 'City',
    dateRange: str(o.dateRange),
    startDate: /^\d{4}-\d{2}-\d{2}$/.test(startDate) ? startDate : '',
    endDate: /^\d{4}-\d{2}-\d{2}$/.test(endDate) ? endDate : startDate,
    slotOptions: parseSlotOptions(o.slotOptions),
    mustSee: Array.isArray(o.mustSee)
      ? o.mustSee
          .map((m) => parseMustSee(m))
          .filter((m): m is AggregatedMustSeeItem => m != null)
      : [],
    hotels: Array.isArray(o.hotels)
      ? o.hotels
          .map((h) => parseHotel(h))
          .filter((h): h is AggregatedHotelItem => h != null)
      : [],
    recommendedStayArea: parseStayArea(o.recommendedStayArea),
    activityCatalog: Array.isArray(o.activityCatalog)
      ? o.activityCatalog
          .map((a): AggregatedCatalogActivity | null => {
            const r = asRecord(a);
            const title = str(r.title).trim();
            if (!title) return null;
            return {
              id: str(r.id) || newId(),
              title,
              normalizedTitle: str(r.normalizedTitle, title),
              description: strOrNull(r.description),
              area: strOrNull(r.area),
              url: strOrNull(r.url),
              tags: strArray(r.tags),
              matchScore: num(r.matchScore, 0),
            };
          })
          .filter((a): a is AggregatedCatalogActivity => a != null)
      : null,
    mealCatalog: Array.isArray(o.mealCatalog)
      ? o.mealCatalog
          .map((m): AggregatedCatalogMeal | null => {
            const r = asRecord(m);
            const title = str(r.title).trim();
            if (!title) return null;
            return {
              id: str(r.id) || newId(),
              title,
              normalizedTitle: str(r.normalizedTitle, title),
              description: strOrNull(r.description),
              cuisine: strOrNull(r.cuisine),
              area: strOrNull(r.area),
              url: strOrNull(r.url),
              tags: strArray(r.tags),
              matchScore: num(r.matchScore, 0),
            };
          })
          .filter((m): m is AggregatedCatalogMeal => m != null)
      : null,
    primaryItineraryDays: parseItineraryDays(o.primaryItineraryDays),
    summary: strOrNull(o.summary),
    generatedDate: strOrNull(o.generatedDate),
  };
}

/** Normalize title for selection / dedupe — lowercase, strip punctuation noise. */
export function normalizePlanTitle(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}
